import { useState } from "react";
import {
  ExclamationTriangleIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import QuickAvailabilityModal from "../Availability/QuickAvailabilityModal";
import ReenableAvailabilityModal from "../Availability/ReenableAvailabilityModal";

// Renders one amber attention strip per delivery type (0, 1, or 2 total)
// above the services list, for active services that can't take bookings
// because that type's availability is missing or turned off. Each strip has
// exactly two states:
//   missing     → open the locked create form for that type (QuickAvailabilityModal)
//   turned off  → open ReenableAvailabilityModal, listing every disabled
//                 availability of that type with a one-tap toggle each
//
// Props:
//   unbookable      — active services that can't be booked
//   bookabilityById — { [serviceId]: { bookable, missing } }
//   providerTypes   — the object returned by deriveProviderAvailabilityTypes
//                      (providerHasMobile/Fixed, inactiveMobile/FixedList, ...)
const ServiceAttentionStrip = ({
  unbookable = [],
  bookabilityById = {},
  providerTypes = {},
}) => {
  const [quickSetup, setQuickSetup] = useState(null); // "mobile" | "fixed" | null
  const [reenableKind, setReenableKind] = useState(null); // "mobile" | "fixed" | null

  const {
    inactiveMobileList = [],
    inactiveFixedList = [],
  } = providerTypes;

  if (!unbookable.length) return null;

  // Which delivery types are missing across the unbookable set. "any"
  // (hybrid with no availability at all) counts as both.
  const missings = unbookable.map((s) => bookabilityById[s._id]?.missing);
  const missingMobile = missings.some((m) => m === "mobile" || m === "any");
  const missingFixed = missings.some((m) => m === "fixed" || m === "any");

  const countFor = (kind) =>
    unbookable.filter((s) => {
      const missing = bookabilityById[s._id]?.missing;
      return missing === kind || missing === "any";
    }).length;

  const strips = [];

  if (missingMobile) {
    const turnedOff = inactiveMobileList.length > 0;
    strips.push({
      kind: "mobile",
      count: countFor("mobile"),
      message: turnedOff
        ? "Your mobile services aren't bookable — a mobile availability is turned off."
        : "Your mobile services aren't bookable yet — you have no active mobile availability.",
      ctaLabel: turnedOff ? "Turn availability on" : "Add mobile availability",
      onClick: turnedOff
        ? () => setReenableKind("mobile")
        : () => setQuickSetup("mobile"),
    });
  }

  if (missingFixed) {
    const turnedOff = inactiveFixedList.length > 0;
    strips.push({
      kind: "fixed",
      count: countFor("fixed"),
      message: turnedOff
        ? "Your location services aren't bookable — your location is turned off."
        : "Your location services aren't bookable yet — you have no location set up.",
      ctaLabel: turnedOff ? "Turn availability on" : "Add a location",
      onClick: turnedOff
        ? () => setReenableKind("fixed")
        : () => setQuickSetup("fixed"),
    });
  }

  return (
    <>
      <div className="flex flex-col gap-y-3">
        {strips.map((strip) => (
          <div
            key={strip.kind}
            className="flex flex-col gap-y-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-card shadow-hairline border border-ink-100 border-l-4 border-l-signal-amber-500 px-4 py-3"
          >
            <div className="flex items-center gap-x-2.5 min-w-0">
              <ExclamationTriangleIcon className="size-5 text-signal-amber-500 flex-shrink-0" />
              <span className="inline-flex items-center justify-center size-5 rounded-full bg-signal-amber-100 text-xs font-bold text-signal-amber-600 flex-shrink-0">
                {strip.count}
              </span>
              <p className="text-sm font-medium text-ink-900">{strip.message}</p>
            </div>

            <button
              onClick={strip.onClick}
              className="flex-shrink-0 self-start sm:self-auto inline-flex items-center gap-x-1 rounded-pill bg-signal-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-signal-amber-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-amber-500 transition-colors"
            >
              {strip.ctaLabel}
              <ChevronRightIcon className="size-3.5" />
            </button>
          </div>
        ))}
      </div>

      <QuickAvailabilityModal
        open={!!quickSetup}
        tabGroup={quickSetup}
        onClose={() => setQuickSetup(null)}
      />

      <ReenableAvailabilityModal
        open={!!reenableKind}
        kind={reenableKind}
        availabilities={
          reenableKind === "mobile" ? inactiveMobileList : inactiveFixedList
        }
        onClose={() => setReenableKind(null)}
      />
    </>
  );
};

export default ServiceAttentionStrip;
